
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import PageLayout from "@/components/layout/PageLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { AppRole } from "@/types/database/auth";
import { RoleSelect } from "@/components/admin/employees/form/RoleSelect";
import { ExtendedRole } from "@/components/admin/employees/types";

const AuthPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<ExtendedRole>("user");
  const [loading, setLoading] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const checkUser = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Session error:", error);
          return;
        }
        
        if (session?.user) {
          // Check role before redirecting
          const { data: roleData, error: roleError } = await supabase
            .rpc('get_user_role_safely', {
              user_id_input: session.user.id
            });

          if (roleError) {
            console.error("Error fetching role:", roleError);
            return;
          }

          const redirectPath = roleData === 'admin' ? '/admin' : '/dashboard';
          console.log("User already logged in, redirecting to:", redirectPath);
          navigate(redirectPath);
        }
      } catch (error) {
        console.error("Error checking session:", error);
      }
    };
    
    checkUser();
  }, [navigate]);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isSignUp) {
        // Validate required fields for signup
        if (!firstName || !lastName) {
          throw new Error("First name and last name are required");
        }
        
        console.log("Starting signup with data:", {
          email,
          first_name: firstName,
          last_name: lastName,
          phone: phone || '',
          role
        });
        
        // Prepare user metadata with all required fields
        const userMetadata = {
          first_name: firstName,
          last_name: lastName,
          phone: phone || ''
        };
        
        console.log("User metadata being set:", userMetadata);
        
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: userMetadata
          }
        });

        if (error) throw error;

        if (data.user) {
          console.log("User created successfully:", data.user.id);
          console.log("User metadata:", data.user.user_metadata);
          
          // Explicitly update the profiles table
          const profileData = {
            id: data.user.id,
            first_name: firstName,
            last_name: lastName,
            email: email,
            phone: phone || '',
            street: '',
            city: '',
            state: '',
            zip_code: ''
          };
          
          console.log("Explicitly creating/updating profile with data:", profileData);
          
          const { error: profileError } = await supabase
            .from('profiles')
            .upsert(profileData);
            
          if (profileError) {
            console.error("Error updating profile:", profileError);
          } else {
            console.log("Profile updated successfully");
          }
          
          // Verify profile was created correctly
          const { data: verifyProfile, error: verifyError } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', data.user.id)
            .single();
            
          if (verifyError) {
            console.error("Error verifying profile:", verifyError);
          } else {
            console.log("Verified profile data:", verifyProfile);
            console.log("First name:", verifyProfile.first_name);
            console.log("Last name:", verifyProfile.last_name);
            console.log("Phone:", verifyProfile.phone);
          }
          
          // After successful signup, update the role in user_roles table
          // Convert 'client' role to 'user' for database compatibility
          const dbRole: AppRole = role === 'client' ? 'user' : role as AppRole;
          
          const { error: roleError } = await supabase
            .rpc('update_user_role_safely', {
              user_id_input: data.user.id,
              new_role: dbRole
            });

          if (roleError) {
            console.error("Error setting user role:", roleError);
            throw roleError;
          }

          toast({
            description: "Please check your email for the confirmation link.",
          });
        }
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        if (data.user) {
          console.log("Successfully signed in, checking role...");
          
          // Use RPC function to safely check role
          const { data: roleData, error: roleError } = await supabase
            .rpc('get_user_role_safely', {
              user_id_input: data.user.id
            });

          if (roleError) {
            console.error("Error fetching role:", roleError);
            throw roleError;
          }

          console.log("Raw role data received:", roleData);
          console.log("Is admin?", roleData === 'admin');
          
          // Navigate based on role with strict comparison
          if (roleData === 'admin') {
            console.log("Admin user detected, redirecting to admin dashboard");
            navigate('/admin');
          } else {
            console.log("Regular user detected, redirecting to user dashboard");
            navigate('/dashboard');
          }
          
          toast({
            description: "Successfully signed in!",
          });
        }
      }
    } catch (error: any) {
      console.error("Authentication error:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "An error occurred during authentication",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <div className="flex min-h-screen items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <Card className="w-full max-w-md space-y-8 p-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold">
              {isSignUp ? "Create an account" : "Sign in to your account"}
            </h2>
          </div>

          <form onSubmit={handleAuth} className="space-y-6">
            {isSignUp && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className="mt-1"
                      placeholder="Enter your first name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="mt-1"
                      placeholder="Enter your last name"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="mt-1"
                    placeholder="Enter your phone number (optional)"
                  />
                </div>

                <div>
                  <Label htmlFor="role">Role</Label>
                  <RoleSelect 
                    value={role} 
                    onRoleChange={(value) => setRole(value)} 
                  />
                </div>
              </>
            )}

            <div>
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1"
                placeholder="Enter your password"
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? "Processing..." : isSignUp ? "Sign Up" : "Sign In"}
            </Button>

            <div className="text-center">
              <Button
                type="button"
                variant="link"
                onClick={() => setIsSignUp(!isSignUp)}
                className="text-sm"
              >
                {isSignUp
                  ? "Already have an account? Sign in"
                  : "Don't have an account? Sign up"}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </PageLayout>
  );
};

export default AuthPage;
